import { defineStore } from 'pinia';
import { setMeta } from "~/utils/meta";
import * as qs from "qs";
import { ProductDetailEntity } from "~/entities/shop/ProductDetailEntity";
import { ProductReviewDetailType } from "~/types/shop/review/ProductReviewDetailType";
import { ProductReviewDetailEntity } from "~/entities/shop/review/ProductReviewDetailEntity";

export const useProductReviewDetailPageStore = defineStore({
    id: 'useProductReviewDetailPageStore',
    state: (): ProductReviewDetailType => ({
        title: undefined,
        date: undefined,
        category: undefined,
        slug: undefined,
        content: undefined,
        products: undefined,
        url: undefined,
        error: undefined,
    }),
    getters: {
        getStructuredData(state: ProductReviewDetailType): object[] {
            return [
                {
                    "@context": "https://schema.org",
                    "@type": "BreadcrumbList",
                    "itemListElement": [
                        {
                            "@type": "ListItem",
                            "position": 1,
                            "name": "Reviews",
                            "item": this.$useRuntimeConfig.public.baseUrl + "/reviews",
                        },
                        {
                            "@type": "ListItem",
                            "position": 2,
                            "name": state.category?.title,
                            "item": this.$useRuntimeConfig.public.baseUrl + state.category?.url,
                        },
                        {
                            "@type": "ListItem",
                            "position": 3,
                            "name": state.title,
                        }
                    ]
                }
            ];
        },
    },
    actions: {
        clear() {
            this.title = undefined;
            this.date = undefined;
            this.category = undefined;
            this.slug = undefined;
            this.content = undefined;
            this.products = undefined;
            this.url = undefined;
            this.error = undefined;
        },

        async fetch(slug: string) {
            try {
                const query = {
                    fields: [
                        'slug',
                        'title',
                        'publishedAt',
                    ],
                    sort: ['publishedAt:desc'],
                    populate: {
                        products: {
                            populate: {
                                product: {
                                    populate: {
                                        images: {
                                            populate: '*',
                                        },
                                        product_category: {
                                            populate: '*',
                                        },
                                        ratings: {
                                            populate: '*',
                                        },
                                        content: {
                                            populate: '*',
                                        },
                                    },
                                },
                            },
                        },
                        product_review_category: {
                            populate: '*',
                        },
                        content: {
                            populate: '*',
                        },
                        seo: {
                            populate: ['metaImage']
                        }
                    },
                    filters: {
                        slug,
                    },
                };

                const response = await this.$useNuxtApp.axios.get(`/product-reviews?${qs.stringify(query, {
                    encodeValuesOnly: true
                })}`);
                if (response?.data?.data === undefined || !response?.data?.data.length || this.error !== undefined) {
                    this.error = createError({statusCode: 404, statusMessage: 'Page Not Found'});
                    return false;
                }

                const data = response.data.data[0];
                Object.assign(
                    this,
                    ProductReviewDetailEntity(data, this.$useRuntimeConfig),
                );

                if (data.attributes?.seo) {
                    if (data.attributes.seo?.structuredData === undefined || data.attributes.seo?.structuredData === null) {
                        data.attributes.seo.structuredData = this.getStructuredData;
                    }

                    setMeta(data.attributes.seo, this.$useRuntimeConfig, this.$useRoute);
                }
            } catch (error) {
                console.log(error.message);
                this.error = createError({ statusCode: 500, statusMessage: 'Internal error' });
                return false;
            }

            return true;
        }
    },
});
